import {inject, Injectable} from '@angular/core';
import {
    FormArray,
    FormGroup,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { RestUrlsConfig, GenericConfig } from '@signature-it/ngx-generic/config';
import {catchError, map, tap, switchMap, mergeMap, shareReplay} from 'rxjs/operators';
import { Observable, throwError, Subject, BehaviorSubject, of } from 'rxjs';
import { throttleTime, debounceTime }  from 'rxjs/operators';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { EventBusService } from './event-bus.service';
import { ILanguage } from '@signature-it/ngx-generic/interfaces';
import { ComponentDTO, FormDTO, RuleFormDTO, FormStepDTO, FormField, FormFieldFactory, ComponentFullDTO, FormSettingsDTO, CngDataDTO } from '@signature-it/ngx-generic/models';
import { EventData, EventName } from '@signature-it/ngx-generic/classes';
import { FieldType } from '@signature-it/ngx-generic/enums';
import { PageNotificationService } from './page-notification';
import { CommonService } from './common.service';
import { FormValidatorsService } from './form-validators.service';
import {RuleFormRequestDTO} from "@signature-it/ngx-generic/models";
import { cloneDeep } from 'lodash';
import {RouteService} from "./route.service";
import {Router} from "@angular/router";

declare let _: any;


@Injectable({
    providedIn: 'root'
})
export class FormService extends CommonService {

    protected getSolrUrl = `${this.restUrlsConf.appURL}/public/solr`;
    protected postProductCngOptionsUrl = `/public/product/cng-products-options`;
    protected getAddCngProductsUrl = `/public/products/cng/add.json`;
    protected getCngProductsUrl = `/public/product/cng-products`;
    protected getBackendReflactionRowsUrl = `/public/form/backendreflaction/get-table-rows`
    protected removeCngProductUrl = `/public/product/remove/cng`;
    protected addCngProductUrl = `/public/product/cng/add.json`;
    protected checkUniqueFieldUrl = `public/cng/unique-field`;
    protected getCngProductAttrUrl  = `public/cng/attribute`;
    private allFields: any;
    private solrComponents: { [path: string]: Observable<ComponentDTO[]> } = {};
	public onFieldValuesChanged = new Subject<any>();
	public onFieldValuesChanged$: any;
	public onDefaultValuesInit = new Subject<any>();
	public onDefaultValuesInit$: any;
	public onDisableField = new Subject<any>();
	public onDisableField$: any;
	public fieldsValues$ = new BehaviorSubject<any>(null);
	public values$: any;
    public values = new BehaviorSubject<any>(null);
	public cngData$ = new BehaviorSubject<CngDataDTO>(null);
	public afterSaveCng$ = new BehaviorSubject<CngDataDTO>(null);
	public onCustomElementFieldLoaded = new Subject<any>();
	public onCustomElementFieldLoaded$: any;
	public waitingForAsyncAction$ = new BehaviorSubject(false);
    public ruleForm$ = new BehaviorSubject<RuleFormDTO>(null);
    public onRequiredField$ = new Subject<any>();
    mediaArray: {} = {};
    protected ruleFormSubscription$ = [];
    protected routeService : RouteService = inject(RouteService);
    private router: Router = inject(Router);
    constructor(protected http: HttpClient,
                protected restUrlsConf: RestUrlsConfig,
                protected notifySvc: PageNotificationService,
                protected eventBusService: EventBusService) {
        super(http, restUrlsConf, notifySvc);
        this.routeService.currentUrl$.subscribe(currentUrl => {
            this.mediaArray = {};
        })
		this.onFieldValuesChanged$ = this.onFieldValuesChanged.asObservable();
		this.onDefaultValuesInit$ = this.onDefaultValuesInit.asObservable();
		this.onDisableField$ = this.onDisableField.asObservable();
		this.onCustomElementFieldLoaded$ = this.onCustomElementFieldLoaded.asObservable();
		// this.values$ = this.values.asObservable();
		this.values$ = this.values.pipe(
			debounceTime(100),
			mergeMap(v => of(v)));
    }

    getRulesForm(request: RuleFormRequestDTO): Observable<any> {
        let rule = request.ruleId ? `/${request.ruleId}`: '';
        let test =  request.testId ? `&testId=${ request.testId}`: '';
        let pidForDefault =  request.pidForDefaultValues ? `&pidForDefaultValues=${ request.pidForDefaultValues}`: '';
        let p =  request.isTestScenario ? '': '/public';
        let q =  request.forceState? `&forceState=${ request.forceState}`: '';
        return this.http.get(`${this.restUrlsConf.appURL}${p}/rules-form/${request.productId}${rule}?lang=${request.lang.code}${q}${test}${pidForDefault}`)
            .pipe(map((res: any) => {
                return request.ruleFormDTO.create(res);
            }),
            catchError((error: any) => {
                this.router.navigate(['/404']);
                return throwError(error);
            }));
    }

    addCngProduct(data: any, originReq) {
        return this.http.post(`${this.restUrlsConf.appURL}/${this.addCngProductUrl}`, JSON.stringify( {data , originReq}))
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }
    addCngProducts(data) {
        return this.http.post(`${this.restUrlsConf.appURL}/${this.getAddCngProductsUrl}`, JSON.stringify(data));
    }
    removeCngProduct(data: any) {
        return this.http.post(`${this.restUrlsConf.appURL}/${this.removeCngProductUrl}`, JSON.stringify( {data}))
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }

    getRuleFormFromStateOrApi(request: RuleFormRequestDTO): Observable<any> {
        const key = `${request.productId}-${request.pidForDefaultValues }`;
        if(!this.ruleFormSubscription$[key]) {
            this.ruleFormSubscription$[key] = this.getRulesForm(request).pipe(shareReplay(1));
        }
        return this.ruleFormSubscription$[key];
    }

    getComponentsFromSolr(lang: ILanguage, componentGroupId, compSolrQuery, settings: FormSettingsDTO): Observable<ComponentDTO[]> {
        const fl = settings.hasFcc && settings.fccFL ? `&fl=${settings.fccFL.join()},product_id_i,comp_*,product_pic_s` : '';
        const path = `fq[]=lang_code_s:${lang.code}&rows=5000&sort=comp_order_i+asc&fq[]=group_id_i:${componentGroupId}${compSolrQuery}${fl}`;

        if (this.solrComponents[path]) {
            return this.solrComponents[path].pipe(
                map((components) => cloneDeep(components)) // Clone the array of components before returning
            );
        }

        const request$ = this.http.get(`${this.getSolrUrl}?${path}`).pipe(
            map((res) => {
                const response = res['response'] || {};
                const comp = fl ? ComponentFullDTO.createFromArray(response.docs) : ComponentDTO.createFromArray(response.docs);
                return comp;
            }),
            catchError((error) => throwError(error)),
            shareReplay(1)
        );

        this.solrComponents[path] = request$;

        return request$.pipe(
            map((components) => cloneDeep(components)) // Clone the array of components before returning
        );
    }

    getCngProductsOptions(cngId, conditions): Observable<any> {
        return this.http.post(`${this.restUrlsConf.appURL}/${this.postProductCngOptionsUrl}`, JSON.stringify({cngId, conditions}));
    }
    getCngProducts(data) {
        return this.http.post(`${this.restUrlsConf.appURL}/${this.getCngProductsUrl}`, JSON.stringify(data));
    }

    toFormGroup(form: FormDTO) {
        let group = new UntypedFormGroup({});
        form.steps.forEach((step: FormStepDTO) => {
            if(form.settings.markNextField) {
				form.validatorFn.push(this.isNextField.bind(this));
            }
            group.addControl(step.label, new UntypedFormGroup({}, { validators: form.validatorFn }));
            this.createStepFields(step, group.controls[step.label], form, group);
        });
        return group;
    }

    createStepFields(step, group, form, rootGroup) {
		if(step.steps){
			step.steps.forEach((step: FormStepDTO) => {
				group.addControl(step.label, new UntypedFormGroup({}, { validators: form.validatorFn }));
				this.createStepFields(step, group.controls[step.label], form, rootGroup);
			});
		} else {
			step.fields?.forEach(formField => {
				// if(formField.isFictive) return;
				let validators = this.buildValidators(formField, this.validatorJs(formField.validatorJs, rootGroup));

				let formControl = validators.length ?
					new UntypedFormControl(formField.getValue(), validators) :
					new UntypedFormControl(formField.getValue());

				group.addControl(formField.key, formControl);
			});
        }
    }

    validatorJs(js: string, rootGroup): ValidatorFn {
        if(!js) return null;
        return (control: UntypedFormControl): ValidationErrors | null => {

            let r = this.evalJs(js,  this.flattenFormGroup(rootGroup));
            if (r) return {customJsValid: r};
            return null
        };
    }

    flattenFormGroup(formGroup) {
        if (!formGroup) return {};
        const flatObject: { [key: string]: any } = {};
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.controls[key];
            if (control instanceof FormGroup || control instanceof FormArray) {
                Object.assign(flatObject, this.flattenFormGroup(control));
            } else {
                flatObject[key] = control.value;
            }
        });
        return flatObject;
    }

    evalJs(js, ans) {
        let c = '(function() {' + js + '})()';
        let r = new Function(`let ans = ${JSON.stringify(ans)};
                return ${c};`)();
        return r;
    }

    buildValidators(formField, validatorJs = null) {
        let validators = [];
        if(formField.required) {
            validators.push(Validators.required);
        }
        if(formField.validator) {
            validators.push(Validators.pattern(formField.validator));
        }
        if(formField.type === FieldType.ID) {
            validators.push(FormValidatorsService.isValidIsraeliID);
        }
        if (formField.type === FieldType.CREDIT_CARD_NUMBER) {
            validators.push(FormValidatorsService.isValidCreditCardNumber)
        }
        if (formField.type === FieldType.CREDIT_CARD_CVV) {
            validators.push(FormValidatorsService.isValidCardCvv)
        }
        if (validatorJs) {
            validators.push(validatorJs);
        }
        return validators;
    }

    saveTestScenarioFrm(data: any): Observable<any> {
        return this.http.post(`${this.restUrlsConf.appURL}/rules-form/save-test-scenario`, JSON.stringify(data))
            .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }

    getTestScenarioFrm(testId: number): Observable<any> {
        return this.http.get(`${this.restUrlsConf.appURL}/rules-form/get-test-scenario/${testId}`)
            .pipe(map((res) => {
                return res;
            }), catchError((error: any) => throwError(error)));
    }

    getAllFields(lang: ILanguage): Observable<FormField[]> {
        return new Observable(observer => {
            if (this.allFields) {
                observer.next(_.map(this.allFields, _.clone));
                return observer.complete();
			}
            this.http.get<ILanguage[]>(`${this.restUrlsConf.appURL}/public/rules-form/all/fields?lang=${lang.code}`).subscribe(res => {
                let formFields: FormField[] = [];
                res.forEach(field => {
                    formFields.push(FormFieldFactory.create(field));
                }, formFields);
                this.allFields = formFields;
                observer.next(_.map(formFields, _.clone));
				return observer.complete();
            }, error => throwError(error));
        });
    }

	isNextField(control: UntypedFormGroup): null {
        let flag = 0;
		Object.keys(control.controls).forEach(key => {
            if(control.get(key).errors && control.get(key).errors['nextField']) {
               delete control.get(key).errors['nextField'];
            }
			// if(!FormField.isValidValue(control.controls[key].value) && !control.get(key).errors.hidden && !flag) {
			if(!FormField.isValidValue(control.controls[key].value) && !flag) {
				control.get(key).setErrors({...control.get(key).errors, ...{ 'nextField': true }});
                flag = 1;
                this.eventBusService.emit(new EventData(EventName.DYNAMIC_FIELD_CHANGED_ERRORS, true));
            }
		});
		return null;
	};
    getBackendReflactionFrom(collectionVar) {
        return this.http.get(`${this.restUrlsConf.appURL}/public/form/backendreflaction/${collectionVar}`)
            .pipe(map((res: any) => {
                return {
                    form: FormDTO.create(res['form']),
                }
            }), catchError( (error: any) => throwError(error)));
    }
    getBackendReflactionRows(data){
        return this.http.post(`${this.restUrlsConf.appURL}/${this.getBackendReflactionRowsUrl}`, JSON.stringify(data));
    }

    getCollectionForm(collectionVar) {
        return this.http.get(`${this.restUrlsConf.appURL}/public/form/collection/${collectionVar}`)
            .pipe(map((res: any) => {
                return {
                    form: FormDTO.create(res['form']),
                    template: res['template']
                }
            }), catchError( (error: any) => throwError(error)));
    }

    saveCollectionForm(collectionVar, data) {
        return this.http.post(`${this.restUrlsConf.appURL}/public/form/collection/${collectionVar}`, JSON.stringify(data))
            .pipe(catchError((error: any) => throwError(error)));
    }

	updateValues(values) {
		this.values.next(values);
	}

	updateFieldsValues(values) {
		this.fieldsValues$.next(values);
	}

	updateCngData(cngData) {
    	if(GenericConfig.debugMode) {
    		console.log(cngData);
		}
        this.cngData$.next(cngData);
    }

	afterSaveCng(cngData) {
		this.afterSaveCng$.next(cngData);
	}

    saveSingatureImage(data: any): Observable<any> {
           return this.http.post(`${this.restUrlsConf.appURL}/public/product/saveSignatureImage.json`, JSON.stringify(data))
               .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }

    getMediaData(id) {
        if(!this.mediaArray[id]) {
            this.mediaArray[id] =  this.http.get(`${this.restUrlsConf.appURL}/public/media/${id}.json`).pipe(
                shareReplay(1)
            );
        }
        return this.mediaArray[id];
    }

    checkUniqueField(data) {
        return this.http.post(`${this.restUrlsConf.appURL}/${this.checkUniqueFieldUrl}`, JSON.stringify(data));
    }

    getCngProductAttr(data) {
        return this.http.post(`${this.restUrlsConf.appURL}/${this.getCngProductAttrUrl}`, JSON.stringify(data));
    }

}
